import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getApiUrl } from "../authConfig";
import Navbar from "../components/Common/Navbar";
import Footer from "../components/Common/Footer";
import Loader from "../components/Common/Loader";
import portalBannerImg from "../Assets/portal-banner.png";

const CustomerTaxPortalPage = () => {
  const { token } = useParams();
  const [documentData, setDocumentData] = useState(null);
  
  // UX States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [refreshingDoc, setRefreshingDoc] = useState(false);

  // Form States
  const [signatureName, setSignatureName] = useState("");
  const [locationStr, setLocationStr] = useState("");
  const [agreedToFile, setAgreedToFile] = useState(false);

  // Abstracted to allow manual refreshing if the MS Graph session expires
  const fetchPreviewUrl = useCallback(async () => {
    const previewResponse = await fetch(getApiUrl(`/v1/public/generate-preview-url/${token}`), {
      method: 'POST',
    });
    if (!previewResponse.ok) throw new Error("Could not generate the document preview.");
    const previewData = await previewResponse.json();
    let secureUrl = previewData.preview_url;
        
    // Append the zoom parameter to force Window Width
    secureUrl += (secureUrl.includes('?') ? '&' : '?') + 'wdZoom=FitWidth';
    return secureUrl;
  }, [token]);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await fetch(getApiUrl(`/v1/public/review-tax/${token}`));
        if (!response.ok) {
          throw new Error("This document link is invalid or has expired.");
        }
        let data = await response.json();

        // Dynamically generate the cookie-less preview URL for the iframe
        if (data.onedrive_item_id) {
          data.preview_url = await fetchPreviewUrl();
        }
        setDocumentData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchDocument();
  }, [token, fetchPreviewUrl]);

  const handleRefreshDocument = async () => {
    setRefreshingDoc(true);
    setIframeLoaded(false);
    try {
      const newUrl = await fetchPreviewUrl();
      setDocumentData(prev => ({ ...prev, preview_url: newUrl }));
    } catch (err) {
      toast.error("Could not refresh document. Please reload the page.");
    } finally {
      setRefreshingDoc(false);
    }
  };

  const handleSubmitSignature = async () => {
    if (!signatureName.trim() || !locationStr.trim() || !agreedToFile) {
      toast.warn("Please fill out your name, location, and agree to the filing to provide consent.");
      return;
    }

    try {
      setLoading(true);

      let publicIp = "Unknown";
      let resolvedLocation = "Unknown";
      
      // Fetch telemetry with a 3-second timeout so it doesn't hang the submission
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        const geoRes = await fetch("https://ipapi.co/json/", { signal: controller.signal });
        clearTimeout(timeoutId);
        
        if (geoRes.ok) {
          const geoData = await geoRes.json();
          publicIp = geoData.ip || "Unknown";
          resolvedLocation = [geoData.city, geoData.region, geoData.country_name].filter(Boolean).join(", ");
        }
      } catch (e) {
        console.warn("Telemetry fetch timed out or failed. Proceeding with submission.", e);
      }

      const response = await fetch(getApiUrl(`/v1/public/review-tax/${token}/sign`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          typed_name: signatureName,
          location: locationStr,
          agreed_to_file: agreedToFile,
          consent_timestamp: new Date().toLocaleString("en-US", { timeZoneName: "short" }),
          public_ip: publicIp,
          resolved_location: resolvedLocation,
          device_platform: navigator.platform,
          browser_engine: navigator.userAgent
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Failed to submit signature.");
      }
      
      setSuccess(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
      if (loading && !documentData) return <Loader variant="overlay" show={true} text="Loading Secure Document..." />;
      
      if (error) {
        return (
          <div className="container py-5 text-center">
            <div className="alert alert-danger shadow-sm rounded-4" style={{ maxWidth: "600px", margin: "0 auto" }}>
              <h4 className="alert-heading fw-bold">Access Denied</h4>
              <p className="mb-0">{error}</p>
            </div>
          </div>
        );
      }

      if (success) {
        return (
          <div className="container py-5 text-center">
            <div className="alert alert-success shadow border-0 rounded-4" style={{ maxWidth: "600px", margin: "0 auto", padding: "3rem 2rem" }}>
              <div className="display-4 mb-3">✅</div>
              <h3 className="fw-bold">Document Successfully Signed</h3>
              <p className="lead text-muted">Your tax return authorization has been recorded securely and cryptographically sealed.</p>
              <p className="text-muted mt-4 small">You may now safely close this window.</p>
            </div>
          </div>
        );
      }

      const displayFileName = documentData?.file_name 
        ? documentData.file_name.replace(/\.[^/.]+$/, "") 
        : documentData?.tax_year && documentData?.tax_type 
          ? `${documentData.tax_year} ${documentData.tax_type} Tax Return` 
          : "Tax Document";

      return (
        <div id="review-section" className="container-fluid px-0 px-xl-4 py-4">
          
          {/* Responsive CSS applied only to this component */}
          <style>
            {`
              .doc-viewer-container { height: 55vh; min-height: 400px; }
              .sign-form-container { height: auto;}
              
              @media (max-width: 768px) {
                .doc-viewer-container { height: 50vh; min-height: 300px; }
                .sign-form-container { height: auto; margin-top: 1rem; }
              }

              @media (min-width: 992px) {
                .doc-viewer-container { height: 80vh; min-height: 600px; }
                .sign-form-container { height: 80vh; }
              }
            `}
          </style>

          {/* Mobile "Jump to Form" Button (Visible only on small screens) */}
          {!documentData.status?.includes("Signed") && (
            <div className="d-lg-none px-3 mb-3 text-center">
              <a href="#signature-form" className="btn btn-outline-primary rounded-pill px-4 btn-sm fw-bold">
                ↓ Skip to Signature
              </a>
            </div>
          )}

          <div className="card shadow-lg border-0 rounded-4 mb-4 overflow-hidden">
            
            <div className="card-header bg-white border-bottom p-3 p-md-4 d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0 fw-bold text-dark">Review & Sign</h4>
                <p className="text-muted mb-0 small mt-1">{displayFileName}</p>
              </div>
              <button 
                className="btn btn-outline-secondary btn-sm rounded-pill px-3 d-none d-md-block" 
                onClick={handleRefreshDocument}
                disabled={refreshingDoc}
              >
                {refreshingDoc ? "Refreshing..." : "↻ Reload Viewer"}
              </button>
            </div>
            
            <div className="card-body p-0">
              <div className="row g-0">
                
                {/* ═══ DOCUMENT VIEWER COLUMN ═══ */}
                <div className="col-lg-8 position-relative bg-secondary bg-opacity-10 doc-viewer-container">
                  
                  {/* Mobile-only fallback header to open in new tab */}
                  <div className="p-2 text-center bg-white d-lg-none border-bottom z-3 position-relative shadow-sm">
                    <p className="mb-2 small text-muted">Hard to read on mobile? Open full screen.</p>
                    <a href={documentData.preview_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm rounded-pill px-4 shadow-sm w-75">
                      Open Document in New Tab
                    </a>
                  </div>

                  {!iframeLoaded && (
                    <div className="position-absolute top-50 start-50 translate-middle text-center text-muted w-100 z-1">
                      <div className="spinner-border text-primary mb-2" role="status"></div>
                      <p className="small fw-bold">Connecting to Secure Vault...</p>
                    </div>
                  )}

                  <iframe
                    src={documentData.preview_url}
                    width="100%"
                    height="100%"
                    className="position-relative z-2"
                    style={{ border: 'none', opacity: iframeLoaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
                    title="Document Preview"
                    onLoad={() => setIframeLoaded(true)}
                    allow="fullscreen"
                  ></iframe>
                </div>

                {/* ═══ SIGNATURE FORM COLUMN ═══ */}
                <div id="signature-form" className="col-lg-4 bg-light border-start overflow-auto sign-form-container">
                  {documentData.status === "eSigned" || documentData.status === "Signed" ? (
                    <div className="p-4 p-md-5 text-center d-flex flex-column justify-content-center h-100">
                      <div className="display-1 text-success mb-3">✓</div>
                      <h4 className="text-success fw-bold mb-0">Document eSigned</h4>
                      <p className="text-muted mt-2">This document has already been signed and locked.</p>
                    </div>
                  ) : (
                    <div className="p-4 p-md-5">
                      
                      <div className="mb-4">
                        <span className="badge bg-primary rounded-pill px-3 py-2 mb-2">Step 1</span>
                        <h5 className="fw-bold">Review Document</h5>
                        <p className="text-muted small">Carefully review the tax document. If the viewer times out, reload the page or click 'Reload Viewer' above.</p>
                      </div>
                      
                      <hr className="my-4 text-secondary opacity-25" />

                      <div>
                        <span className="badge bg-primary rounded-pill px-3 py-2 mb-2">Step 2</span>
                        <h5 className="fw-bold mb-3">Authorize & Consent</h5>
                        
                        <div className="row g-3">
                          <div className="col-12">
                            <label className="form-label fw-bold small text-uppercase text-muted">Legal Name</label>
                            <input 
                              type="text" 
                              className="form-control form-control-lg bg-white rounded-3 fs-6" 
                              placeholder="e.g. John Doe" 
                              value={signatureName} 
                              onChange={(e) => setSignatureName(e.target.value)} 
                            />
                            {signatureName && (
                              <div className="mt-2 p-3 bg-white border rounded text-primary text-center shadow-sm" style={{ fontFamily: '"Caveat", "Dancing Script", cursive', fontSize: '1.5rem', userSelect: 'none' }}>
                                x {signatureName}
                              </div>
                            )}
                          </div>

                          <div className="col-12 mt-3">
                            <label className="form-label fw-bold small text-uppercase text-muted">Signing Location</label>
                            <input 
                              type="text" 
                              className="form-control form-control-lg bg-white rounded-3 fs-6" 
                              placeholder="City, Province" 
                              value={locationStr} 
                              onChange={(e) => setLocationStr(e.target.value)} 
                            />
                          </div>

                          <div className="col-12 mt-4">
                            <div className="bg-white p-3 border rounded-3 shadow-sm d-flex align-items-start gap-3">
                              <input 
                                className="form-check-input flex-shrink-0 mt-1" 
                                type="checkbox" 
                                id="agreeCheck" 
                                checked={agreedToFile} 
                                onChange={(e) => setAgreedToFile(e.target.checked)} 
                                style={{ transform: "scale(1.3)", cursor: "pointer", margin: 0 }} 
                              />
                              <label className="form-check-label small mb-0" htmlFor="agreeCheck" style={{ cursor: "pointer", lineHeight: "1.5" }}>
                                I have reviewed the draft and legally authorize the electronic filing of this return. I understand this acts as my binding digital signature.
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 pt-2 pb-5 pb-lg-0">
                          <button 
                            className="btn btn-success btn-lg rounded-pill shadow fw-bold w-100 d-flex align-items-center justify-content-center" 
                            onClick={handleSubmitSignature} 
                            disabled={loading || !signatureName.trim() || !locationStr.trim() || !agreedToFile}
                            style={{ transition: 'all 0.2s' }}
                          >
                            {loading ? (
                              <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Submitting...</>
                            ) : (
                              "Sign & Submit"
                            )}
                          </button>
                        </div>
                      </div>

                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };

  return (
    <div className="min-vh-100 d-flex flex-column bg-light">
      <Loader />
      <Navbar />
      
      <div 
        className="portal-hero-banner text-white position-relative overflow-hidden text-center px-3 mb-2" 
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.4), rgba(15, 23, 42, 0.7)), url(${portalBannerImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '220px',
          marginTop: '-96px',
          paddingTop: '110px',
          paddingBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div className="container position-relative z-1">
          <h2 className="fw-bold mb-0 text-white" style={{ letterSpacing: '0.5px' }}>
            Secure eSign Gateway
          </h2>
        </div>
      </div>

      <div className="flex-grow-1">
        {renderContent()}
      </div>

      <Footer />
    </div>
  );
};
export default CustomerTaxPortalPage;